import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Plus, Trash2, Pencil, Check, X } from 'lucide-react';

const FLOOR_OPTIONS = ['Basement', 'Lower Ground', 'Ground Floor', 'First Floor', 'Second Floor', 'Third Floor', 'Fourth Floor', 'Attic'];
const FLOOR_USE_OPTIONS = ['Living Accommodation', 'Bedrooms', 'Kitchen/Utility', 'Bathroom/WC', 'Office', 'Retail', 'Storage', 'Circulation', 'Plant', 'Ancillary', 'Mixed'];
const LOCATION_OPTIONS = ['internal', 'balcony', 'terrace', 'yard', 'garage', 'store'];

const emptyRow = {
  floor_level: 'Ground Floor', floor_use: 'Living Accommodation', total_area: '',
  notes: '', location_type: 'internal', included_in_valuation: true,
};

export default function FloorSummaryTable({ rows, onAdd, onUpdate, onDelete }) {
  const [newRow, setNewRow] = useState({ ...emptyRow });
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});

  const handleAdd = () => {
    if (!newRow.floor_level) return;
    onAdd({ ...newRow, total_area: parseFloat(newRow.total_area) || 0 });
    setNewRow({ ...emptyRow });
  };

  const startEdit = (row) => { setEditId(row.id); setEditData({ ...row }); };
  const saveEdit = () => {
    onUpdate(editId, { ...editData, total_area: parseFloat(editData.total_area) || 0 });
    setEditId(null);
  };

  return (
    <div className="space-y-3" data-testid="floor-summary-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="text-left p-2 font-medium text-gray-600">Floor Level</th>
              <th className="text-left p-2 font-medium text-gray-600">Use</th>
              <th className="text-right p-2 font-medium text-gray-600 w-32">Area (m²)</th>
              <th className="text-left p-2 font-medium text-gray-600">Location</th>
              <th className="text-center p-2 font-medium text-gray-600 w-16">Valued</th>
              <th className="text-left p-2 font-medium text-gray-600">Notes</th>
              <th className="p-2 w-20"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.id} className="border-b hover:bg-gray-50/50" data-testid={`floor-row-${row.id}`}>
                {editId === row.id ? (
                  <>
                    <td className="p-1">
                      <Select value={editData.floor_level || ''} onValueChange={v => setEditData({...editData, floor_level: v})}>
                        <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{FLOOR_OPTIONS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="p-1">
                      <Select value={editData.floor_use || ''} onValueChange={v => setEditData({...editData, floor_use: v})}>
                        <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{FLOOR_USE_OPTIONS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="p-1 relative z-10 min-w-[100px]"><Input className="h-7 text-xs text-right" type="number" step="0.01" min="0" value={editData.total_area || ''} onChange={e => setEditData({...editData, total_area: e.target.value})} data-testid="edit-floor-area" /></td>
                    <td className="p-1">
                      <Select value={editData.location_type || 'internal'} onValueChange={v => setEditData({...editData, location_type: v})}>
                        <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{LOCATION_OPTIONS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="p-1 text-center"><input type="checkbox" checked={editData.included_in_valuation !== false} onChange={e => setEditData({...editData, included_in_valuation: e.target.checked})} /></td>
                    <td className="p-1"><Input className="h-7 text-xs" value={editData.notes || ''} onChange={e => setEditData({...editData, notes: e.target.value})} /></td>
                    <td className="p-1 flex gap-1">
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={saveEdit}><Check className="h-3 w-3 text-green-600" /></Button>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setEditId(null)}><X className="h-3 w-3" /></Button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="p-2 font-medium">{row.floor_level}</td>
                    <td className="p-2 text-gray-500">{row.floor_use}</td>
                    <td className="p-2 text-right font-mono font-medium">{row.total_area ? Number(row.total_area).toFixed(2) : '-'}</td>
                    <td className="p-2 text-gray-500 capitalize">{row.location_type || 'internal'}</td>
                    <td className="p-2 text-center">{row.included_in_valuation !== false ? '✓' : '—'}</td>
                    <td className="p-2 text-gray-400 text-xs truncate max-w-[150px]">{row.notes || '-'}</td>
                    <td className="p-1 flex gap-1">
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => startEdit(row)}><Pencil className="h-3 w-3" /></Button>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => onDelete(row.id)}><Trash2 className="h-3 w-3 text-red-400" /></Button>
                    </td>
                  </>
                )}
              </tr>
            ))}
            {/* New row */}
            <tr className="border-b bg-blue-50/30">
              <td className="p-1 overflow-hidden">
                <Select value={newRow.floor_level} onValueChange={v => setNewRow({...newRow, floor_level: v})}>
                  <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{FLOOR_OPTIONS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
              </td>
              <td className="p-1 overflow-hidden">
                <Select value={newRow.floor_use} onValueChange={v => setNewRow({...newRow, floor_use: v})}>
                  <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{FLOOR_USE_OPTIONS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </td>
              <td className="p-1 relative z-10 min-w-[100px]"><Input className="h-7 text-xs text-right" type="number" step="0.01" min="0" placeholder="Area" value={newRow.total_area} onChange={e => setNewRow({...newRow, total_area: e.target.value})} data-testid="new-floor-area" /></td>
              <td className="p-1">
                <Select value={newRow.location_type} onValueChange={v => setNewRow({...newRow, location_type: v})}>
                  <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{LOCATION_OPTIONS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </td>
              <td className="p-1 text-center"><input type="checkbox" checked={newRow.included_in_valuation !== false} onChange={e => setNewRow({...newRow, included_in_valuation: e.target.checked})} /></td>
              <td className="p-1"><Input className="h-7 text-xs" placeholder="Notes" value={newRow.notes} onChange={e => setNewRow({...newRow, notes: e.target.value})} /></td>
              <td className="p-1"><Button size="sm" className="h-7 w-7 p-0" onClick={handleAdd} data-testid="add-floor-btn"><Plus className="h-3.5 w-3.5" /></Button></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
